/**
 * Página Monitoramento: subheader com abas de painéis (lista da tabela monitoramento_paineis),
 * botão "Criar novo" com form inline (Nome + tipo: externo | mapalive | preatendimento),
 * config por painel, Modo TV e rotação automática de painéis no Modo TV.
 */

import React, { useState, useCallback, useEffect, useRef, useMemo } from 'react';
import { Check, ClipboardList, LayoutPanelLeft, Map as MapIcon, Monitor, Plus, Repeat, Settings, X } from 'lucide-react';
import { getApiBaseUrl } from '../../../utils/apiConfig';
import { hasGoAppLicense } from '../../../utils/userFinanceiroAccess';
import { MonitoramentoExternoDashboard } from './MonitoramentoExternoDashboard';
import { MonitoramentoMapAliveDashboard } from './MonitoramentoMapAliveDashboard';
import { MonitoramentoPreAtendimentoDashboard } from './MonitoramentoPreAtendimentoDashboard';
import { MonitoramentoExternoConfigModal } from './MonitoramentoExternoConfigModal';
import { MonitoramentoTvRotationModal } from './MonitoramentoTvRotationModal';
import { MonitoramentoDayFilter, getTodayYmd } from './MonitoramentoDayFilter';
import { MonitoramentoStatusFilter } from './MonitoramentoStatusFilter';
import { MonitoramentoActionsMenu } from './MonitoramentoActionsMenu';
import { MonitoramentoModernTooltip } from './MonitoramentoModernTooltip';
import {
  buildMonitoramentoExportMeta,
  exportMonitoramentoPdf,
  exportMonitoramentoPng,
  exportMapAlivePdf,
  exportMapAlivePng
} from '../utils/monitoramentoDashboardExport';
import { exportMonitoramentoXlsx } from '../utils/monitoramentoTableXlsxExport';
import './MonitoramentoPage.css';
import '../../board/toolbar/BoardClickUpFilterChip.css';
import '../../board/toolbar/BoardToolbarSearchFilters.css';

const TIPO_EXTERNO = 'externo';
const TIPO_MAPALIVE = 'mapalive';
const TIPO_PREATENDIMENTO = 'preatendimento';
/** App Go Premium (clientes_produtos / go_id_produto = 4). */
const GO_PREMIUM_ID_PRODUTO = 4;

const PANEL_TYPE_OPTIONS_BASE = [
  { id: TIPO_EXTERNO, label: 'Atividade técnica', tipo_painel: TIPO_EXTERNO },
  { id: TIPO_MAPALIVE, label: 'LiveMap', tipo_painel: TIPO_MAPALIVE },
  { id: TIPO_PREATENDIMENTO, label: 'Pré-atendimento', tipo_painel: TIPO_PREATENDIMENTO }
];

/** LiveMap só com permissão Go (menu Config) + licença Premium (id_produto = 4). */
function canCreateLiveMapPanel(user) {
  return hasGoAppLicense(user) && Number(user?.go_id_produto) === GO_PREMIUM_ID_PRODUTO;
}

function isAdmin(user) {
  if (!user) return false;
  const t = user.type;
  return t === 1 || t === '1' || String(t) === '1' || t === 'Administrador';
}

function getAuthHeaders() {
  const token = typeof localStorage !== 'undefined' ? localStorage.getItem('authToken') : null;
  return token ? { Authorization: `Bearer ${token}`, 'Content-Type': 'application/json' } : {};
}

function isMapAlivePanel(panel) {
  return panel && String(panel.tipo_painel || '').toLowerCase() === TIPO_MAPALIVE;
}

function isPreAtendimentoPanel(panel) {
  return panel && String(panel.tipo_painel || '').toLowerCase() === TIPO_PREATENDIMENTO;
}

function panelTypeIcon(tipo, size = 12, className) {
  const t = String(tipo || '').toLowerCase();
  const props = { size, strokeWidth: 1.75, 'aria-hidden': true, className };
  if (t === TIPO_MAPALIVE) return <MapIcon {...props} />;
  if (t === TIPO_PREATENDIMENTO) return <ClipboardList {...props} />;
  return <LayoutPanelLeft {...props} />;
}

function panelBreadcrumbLabel(panel) {
  if (isMapAlivePanel(panel)) return 'LiveMap';
  if (isPreAtendimentoPanel(panel)) return 'Pré-atendimento';
  return 'Equipe Externa';
}

function normalizeTvRotation(raw) {
  const ids = Array.isArray(raw?.panel_ids)
    ? raw.panel_ids.map((id) => Number(id)).filter((id) => Number.isFinite(id) && id > 0)
    : [];
  const interval = Math.min(60, Math.max(1, parseInt(raw?.interval_minutes, 10) || 5));
  return {
    enabled: !!raw?.enabled,
    interval_minutes: interval,
    panel_ids: ids,
  };
}

function tvPlaylist(rotation, panels) {
  const existing = new Set((panels || []).map((p) => Number(p.id)));
  return (rotation?.panel_ids || []).map(Number).filter((id) => existing.has(id));
}

function formatTvCountdown(ms) {
  const sec = Math.max(0, Math.ceil(ms / 1000));
  const min = Math.floor(sec / 60);
  const rem = sec % 60;
  return `${String(min).padStart(2, '0')}:${String(rem).padStart(2, '0')}`;
}

export function MonitoramentoPage({ currentUser }) {
  const [panels, setPanels] = useState([]);
  const [panelsLoading, setPanelsLoading] = useState(true);
  const [activePanelId, setActivePanelId] = useState(null);
  const [isNewPanel, setIsNewPanel] = useState(false);
  const [newPanelName, setNewPanelName] = useState('');
  const [newPanelTipo, setNewPanelTipo] = useState('');
  const [createError, setCreateError] = useState(null);
  const [fullscreen, setFullscreen] = useState(false);
  const [tvExitVisible, setTvExitVisible] = useState(false);
  const fullscreenRef = useRef(null);
  const tvExitHideTimerRef = useRef(null);
  const dashboardExportRef = useRef(null);
  const exportDataRef = useRef(null);
  const [configModalOpen, setConfigModalOpen] = useState(false);
  const [configRefreshKey, setConfigRefreshKey] = useState(0);
  const newPanelInputRef = useRef(null);
  const panelMenuRef = useRef(null);
  const [panelMenuOpen, setPanelMenuOpen] = useState(false);
  const [selectedDate, setSelectedDate] = useState(() => getTodayYmd());
  const [statusFilter, setStatusFilter] = useState(null);
  const [statusOptions, setStatusOptions] = useState([]);
  const [tvRotation, setTvRotation] = useState(() => normalizeTvRotation(null));
  const [tvRotationModalOpen, setTvRotationModalOpen] = useState(false);
  const [tvNextAt, setTvNextAt] = useState(null);
  const [tvNow, setTvNow] = useState(() => Date.now());
  const [preServiceEnabled, setPreServiceEnabled] = useState(false);

  const isLiveDate = useMemo(() => selectedDate === getTodayYmd(), [selectedDate]);

  useEffect(() => {
    setStatusFilter(null);
  }, [selectedDate]);

  useEffect(() => {
    setStatusFilter(null);
    setStatusOptions([]);
  }, [activePanelId]);

  useEffect(() => {
    if (!statusFilter || !statusOptions.length) return;
    const validIds = new Set(statusOptions.map((o) => o.id));
    const next = statusFilter.filter((id) => validIds.has(id));
    if (next.length === statusFilter.length) return;
    setStatusFilter(next.length === 0 || next.length >= statusOptions.length ? null : next);
  }, [statusOptions, statusFilter]);

  const handleStatusOptionsChange = useCallback((options) => {
    setStatusOptions(options || []);
  }, []);

  const canEdit = isAdmin(currentUser);
  const canLiveMap = canCreateLiveMapPanel(currentUser);
  const canPreAtendimento = preServiceEnabled;
  const panelTypeOptions = useMemo(
    () => PANEL_TYPE_OPTIONS_BASE.filter((opt) => {
      if (opt.id === TIPO_MAPALIVE) return canLiveMap;
      if (opt.id === TIPO_PREATENDIMENTO) return canPreAtendimento;
      return true;
    }),
    [canLiveMap, canPreAtendimento]
  );

  useEffect(() => {
    if (!canLiveMap && newPanelTipo === TIPO_MAPALIVE) {
      setNewPanelTipo('');
    }
    if (!canPreAtendimento && newPanelTipo === TIPO_PREATENDIMENTO) {
      setNewPanelTipo('');
    }
  }, [canLiveMap, canPreAtendimento, newPanelTipo]);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const baseUrl = getApiBaseUrl();
        const res = await fetch(`${baseUrl}/api/config/vc-sys-config/os-status-inicial`, {
          headers: getAuthHeaders(),
        });
        const json = await res.json().catch(() => ({}));
        if (!cancelled) {
          setPreServiceEnabled(Boolean(json?.success && json?.data?.preServiceEnabled));
        }
      } catch (_) {
        if (!cancelled) setPreServiceEnabled(false);
      }
    })();
    return () => { cancelled = true; };
  }, []);

  const fetchTvRotation = useCallback(async () => {
    try {
      const baseUrl = getApiBaseUrl();
      const res = await fetch(`${baseUrl}/api/monitoramento/externo/tv-rotation`, { headers: getAuthHeaders() });
      const json = await res.json();
      if (json.success && json.data) {
        setTvRotation(normalizeTvRotation(json.data));
      }
    } catch (_) {
      setTvRotation(normalizeTvRotation(null));
    }
  }, []);

  const fetchPanels = useCallback(async () => {
    setPanelsLoading(true);
    setCreateError(null);
    try {
      const baseUrl = getApiBaseUrl();
      const res = await fetch(`${baseUrl}/api/monitoramento/externo/paineis`, { headers: getAuthHeaders() });
      const json = await res.json();
      if (json.success && Array.isArray(json.data)) {
        setPanels(json.data);
        if (json.data.length > 0) {
          setActivePanelId((prev) => (json.data.some((p) => p.id === prev) ? prev : json.data[0].id));
        } else {
          setActivePanelId(null);
        }
      }
    } catch (_) {
      setPanels([]);
    } finally {
      setPanelsLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchPanels();
    fetchTvRotation();
  }, [fetchPanels, fetchTvRotation]);

  useEffect(() => {
    if (!canEdit) {
      setConfigModalOpen(false);
      setIsNewPanel(false);
      setTvRotationModalOpen(false);
    }
  }, [canEdit]);

  useEffect(() => {
    if (isNewPanel && newPanelInputRef.current) {
      newPanelInputRef.current.focus();
    }
  }, [isNewPanel]);

  useEffect(() => {
    if (!panelMenuOpen) return undefined;

    const handlePointerDown = (event) => {
      if (panelMenuRef.current && !panelMenuRef.current.contains(event.target)) {
        setPanelMenuOpen(false);
      }
    };

    document.addEventListener('mousedown', handlePointerDown);
    return () => document.removeEventListener('mousedown', handlePointerDown);
  }, [panelMenuOpen]);

  const handleCriarNovo = useCallback(() => {
    setCreateError(null);
    setIsNewPanel(true);
    setPanelMenuOpen(false);
    setNewPanelName('');
    setNewPanelTipo('');
  }, []);

  const saveNewPanel = useCallback(async () => {
    const nome = (newPanelName || '').trim();
    if (!nome) return;
    if (!newPanelTipo) {
      setCreateError('Selecione o tipo de relatório.');
      return;
    }
    if (newPanelTipo === TIPO_MAPALIVE && !canLiveMap) {
      setCreateError('LiveMap disponível apenas com App Go Premium.');
      return;
    }
    if (newPanelTipo === TIPO_PREATENDIMENTO && !canPreAtendimento) {
      setCreateError('Pré-atendimento disponível apenas com o processo habilitado.');
      return;
    }
    setCreateError(null);
    try {
      const baseUrl = getApiBaseUrl();
      const res = await fetch(`${baseUrl}/api/monitoramento/externo/paineis`, {
        method: 'POST',
        headers: getAuthHeaders(),
        body: JSON.stringify({ nome_painel: nome, tipo_painel: newPanelTipo })
      });
      const json = await res.json();
      if (json.success && json.id) {
        await fetchPanels();
        setActivePanelId(json.id);
        setIsNewPanel(false);
        setNewPanelName('');
        setNewPanelTipo('');
        // Config de técnicos só se aplica a painéis externo/mapalive.
        if (newPanelTipo !== TIPO_PREATENDIMENTO) {
          setConfigModalOpen(true);
        }
      } else {
        setCreateError(json.message || 'Erro ao criar painel');
      }
    } catch (e) {
      setCreateError(e.message || 'Erro ao criar painel');
    }
  }, [newPanelName, newPanelTipo, fetchPanels, canLiveMap, canPreAtendimento]);

  const toggleFullscreen = useCallback(() => {
    setFullscreen((prev) => !prev);
  }, []);

  useEffect(() => {
    if (typeof document === 'undefined') return;
    const handleFsChange = () => setFullscreen(Boolean(document.fullscreenElement));
    document.addEventListener('fullscreenchange', handleFsChange);
    if (fullscreen) {
      const el = fullscreenRef.current;
      if (el && !document.fullscreenElement && el.requestFullscreen) el.requestFullscreen().catch(() => {});
    } else if (document.fullscreenElement && document.exitFullscreen) document.exitFullscreen().catch(() => {});
    return () => document.removeEventListener('fullscreenchange', handleFsChange);
  }, [fullscreen]);

  useEffect(() => {
    if (!fullscreen) {
      setTvExitVisible(false);
      if (tvExitHideTimerRef.current) {
        clearTimeout(tvExitHideTimerRef.current);
        tvExitHideTimerRef.current = null;
      }
      return undefined;
    }

    const hideSoon = () => {
      if (tvExitHideTimerRef.current) clearTimeout(tvExitHideTimerRef.current);
      tvExitHideTimerRef.current = setTimeout(() => setTvExitVisible(false), 2200);
    };

    const onMove = () => {
      setTvExitVisible(true);
      hideSoon();
    };

    window.addEventListener('mousemove', onMove);
    window.addEventListener('mousedown', onMove);
    hideSoon();
    return () => {
      window.removeEventListener('mousemove', onMove);
      window.removeEventListener('mousedown', onMove);
      if (tvExitHideTimerRef.current) {
        clearTimeout(tvExitHideTimerRef.current);
        tvExitHideTimerRef.current = null;
      }
    };
  }, [fullscreen]);

  const activePanel = panels.find((p) => p.id === activePanelId);
  const activeIsMapAlive = isMapAlivePanel(activePanel);
  const activeIsPreAtendimento = isPreAtendimentoPanel(activePanel);
  const activeIsExterno = activePanel && !activeIsMapAlive && !activeIsPreAtendimento;
  const panelTitle = activePanel ? activePanel.nome_painel : 'Monitoramento Externo';
  const rotationPlaylist = useMemo(() => tvPlaylist(tvRotation, panels), [tvRotation, panels]);
  const canRotate = Boolean(tvRotation.enabled && rotationPlaylist.length >= 2);
  const activeInRotation = Boolean(
    canRotate && activePanelId != null && rotationPlaylist.includes(Number(activePanelId))
  );
  const tvKeepAlive = Boolean(fullscreen && activeInRotation);
  const tvIntervalMs = tvRotation.interval_minutes * 60 * 1000;

  useEffect(() => {
    if (!fullscreen || !activeInRotation) {
      setTvNextAt(null);
      return undefined;
    }
    const arm = () => setTvNextAt(Date.now() + tvIntervalMs);
    arm();
    const timer = setInterval(() => {
      setActivePanelId((prev) => {
        const current = Number(prev);
        const idx = rotationPlaylist.indexOf(current);
        if (idx < 0) return prev;
        return rotationPlaylist[(idx + 1) % rotationPlaylist.length];
      });
      arm();
    }, tvIntervalMs);
    return () => clearInterval(timer);
  }, [fullscreen, activeInRotation, rotationPlaylist, tvIntervalMs]);

  useEffect(() => {
    if (!fullscreen || !activeInRotation || tvNextAt == null) return undefined;
    const tick = setInterval(() => setTvNow(Date.now()), 250);
    return () => clearInterval(tick);
  }, [fullscreen, activeInRotation, tvNextAt]);

  const tvCountdown = fullscreen && activeInRotation
    ? formatTvCountdown(tvNextAt != null ? tvNextAt - tvNow : tvIntervalMs)
    : null;
  const exportMeta = useMemo(
    () => buildMonitoramentoExportMeta({
      panelTitle,
      selectedDate,
      statusFilter,
      statusOptions,
      isLive: isLiveDate
    }),
    [panelTitle, selectedDate, statusFilter, statusOptions, isLiveDate]
  );

  const mapAliveExportMeta = useMemo(() => ({
    title: 'Monitoramento — LiveMap',
    filters: [
      { label: 'Painel', value: panelTitle || '—' },
      {
        label: 'Status',
        value: !statusFilter || !statusOptions.length || statusFilter.length >= statusOptions.length
          ? 'Todos'
          : statusFilter
            .map((id) => statusOptions.find((o) => o.id === id)?.label || id)
            .join(', ')
      },
      { label: 'Atualização', value: 'Ao vivo' }
    ]
  }), [panelTitle, statusFilter, statusOptions]);

  const handleExportPdf = useCallback(async () => {
    if (!dashboardExportRef.current || !activePanelId || activeIsPreAtendimento) return;
    if (activeIsMapAlive) {
      await exportMapAlivePdf(dashboardExportRef.current, mapAliveExportMeta);
      return;
    }
    await exportMonitoramentoPdf(dashboardExportRef.current, exportMeta);
  }, [activePanelId, exportMeta, activeIsMapAlive, activeIsPreAtendimento, mapAliveExportMeta]);

  const handleExportPng = useCallback(async () => {
    if (!activePanelId || activeIsPreAtendimento) return;
    if (activeIsMapAlive) {
      if (!dashboardExportRef.current) return;
      await exportMapAlivePng(dashboardExportRef.current);
      return;
    }
    if (!fullscreenRef.current) return;
    await exportMonitoramentoPng(fullscreenRef.current);
  }, [activePanelId, activeIsMapAlive, activeIsPreAtendimento]);

  const handleExportXlsx = useCallback(async () => {
    if (activeIsMapAlive || activeIsPreAtendimento) return;
    const snapshot = exportDataRef.current;
    if (!snapshot?.technicians?.length) return;
    await exportMonitoramentoXlsx({ ...snapshot, exportMeta });
  }, [exportMeta, activeIsMapAlive, activeIsPreAtendimento]);

  const canConfirmNew = Boolean(newPanelName.trim() && newPanelTipo);

  return (
    <div
      className={`monitoramento-page ${fullscreen ? 'monitoramento-page--fullscreen' : ''}`}
      ref={fullscreenRef}
    >
      {!fullscreen && (
        <div className="monitoramento-page-top">
          <header className="ec-header">
            <nav className="ec-breadcrumb" aria-label="Localização em análises">
              <span className="ec-breadcrumb-avatar" aria-hidden="true">A</span>
              <span className="ec-breadcrumb-segment">Monitoramento</span>
              <span className="ec-breadcrumb-sep" aria-hidden="true">/</span>
              <span className="ec-breadcrumb-current">
                {panelTypeIcon(activePanel?.tipo_painel, 13, 'ec-breadcrumb-current-icon')}
                <span className="ec-breadcrumb-current-text">
                  {panelBreadcrumbLabel(activePanel)}
                </span>
              </span>
            </nav>
          </header>

          <div className="filters-bar ec-filters-bar monitoramento-toolbar">
            <div className="filter-buttons">
              <div className="filter-group board-toolbar-filter-group monitoramento-toolbar-left">
                {panelsLoading ? (
                  <span className="monitoramento-views-bar-loading">Carregando painéis...</span>
                ) : (
                  <div className="monitoramento-panel-menu-anchor" ref={panelMenuRef}>
                    <MonitoramentoModernTooltip
                      label="Selecione o painel desejado ou configure"
                      placement="bottom"
                      className="monitoramento-modern-tooltip-host--chip"
                      hideOnClick
                      suppress={panelMenuOpen}
                    >
                      <button
                        type="button"
                        className={['board-clickup-filter-chip', activePanel ? 'has-selection' : '', panelMenuOpen ? 'is-open' : ''].filter(Boolean).join(' ')}
                        aria-expanded={panelMenuOpen}
                        aria-haspopup="menu"
                        aria-label="Selecionar painel"
                        onClick={() => {
                          setPanelMenuOpen((open) => !open);
                          setIsNewPanel(false);
                          setCreateError(null);
                        }}
                      >
                        <span className="board-clickup-filter-chip-icon" aria-hidden="true">
                          {panelTypeIcon(activePanel?.tipo_painel, 13)}
                        </span>
                        <span className="board-clickup-filter-chip-text">
                          <span className="board-clickup-filter-chip-label">Painel</span>
                          <span className="board-clickup-filter-chip-separator">:</span>
                          <span className="board-clickup-filter-chip-value">{activePanel ? activePanel.nome_painel : 'Selecione'}</span>
                        </span>
                      </button>
                    </MonitoramentoModernTooltip>

                    {panelMenuOpen ? (
                      <div className="monitoramento-panel-menu" role="menu" aria-label="Opções de painel">
                        {panels.map((panel) => {
                          const isActive = activePanelId === panel.id;
                          const isPre = isPreAtendimentoPanel(panel);
                          return (
                            <div
                              key={panel.id}
                              className={['monitoramento-panel-menu-item-row', isActive ? 'is-active' : ''].filter(Boolean).join(' ')}
                            >
                              <button
                                type="button"
                                className={['monitoramento-panel-menu-item', isActive ? 'is-active' : ''].filter(Boolean).join(' ')}
                                role="menuitemradio"
                                aria-checked={isActive}
                                onClick={() => {
                                  setActivePanelId(panel.id);
                                  setPanelMenuOpen(false);
                                  setIsNewPanel(false);
                                  setCreateError(null);
                                }}
                              >
                                <span className="monitoramento-panel-menu-item-icon" aria-hidden="true">
                                  {panelTypeIcon(panel.tipo_painel, 12)}
                                </span>
                                <span className="monitoramento-panel-menu-item-label">{panel.nome_painel}</span>
                                {isActive ? <Check size={14} strokeWidth={2.25} aria-hidden className="monitoramento-panel-menu-item-check" /> : null}
                              </button>
                              {canEdit && !isPre ? (
                                <MonitoramentoModernTooltip
                                  label="Configuração do painel"
                                  placement="bottom"
                                  className="monitoramento-modern-tooltip-host--config"
                                >
                                  <button
                                    type="button"
                                    className="monitoramento-panel-menu-config-btn"
                                    onClick={() => {
                                      setActivePanelId(panel.id);
                                      setPanelMenuOpen(false);
                                      setConfigModalOpen(true);
                                    }}
                                    aria-label={`Configuração do painel ${panel.nome_painel}`}
                                  >
                                    <Settings size={14} strokeWidth={2} aria-hidden />
                                  </button>
                                </MonitoramentoModernTooltip>
                              ) : null}
                            </div>
                          );
                        })}
                      </div>
                    ) : null}
                  </div>
                )}

                {canEdit && (
                  <div className="monitoramento-new-panel-anchor">
                    <button
                      type="button"
                      className={['board-clickup-filter-chip', isNewPanel ? 'has-selection' : ''].filter(Boolean).join(' ')}
                      aria-label="Criar novo painel"
                      onClick={handleCriarNovo}
                    >
                      <span className="board-clickup-filter-chip-icon" aria-hidden="true">
                        <Plus size={13} strokeWidth={1.75} />
                      </span>
                      <span className="board-clickup-filter-chip-text">
                        <span className="board-clickup-filter-chip-label">Criar novo</span>
                      </span>
                    </button>
                    {isNewPanel && (
                      <div className="monitoramento-new-panel-form monitoramento-new-panel-form--typed">
                        <input
                          id="monitoramento-new-panel-name"
                          ref={newPanelInputRef}
                          type="text"
                          className="monitoramento-new-panel-input"
                          placeholder="Nome do painel"
                          value={newPanelName}
                          onChange={(e) => { setNewPanelName(e.target.value); setCreateError(null); }}
                          onKeyDown={(e) => {
                            if (e.key === 'Enter') saveNewPanel();
                            if (e.key === 'Escape') {
                              setIsNewPanel(false);
                              setNewPanelName('');
                              setNewPanelTipo('');
                              setCreateError(null);
                            }
                          }}
                          maxLength={30}
                        />
                        <div className="monitoramento-new-panel-types" role="group" aria-label="Tipo de relatório">
                          {panelTypeOptions.map((opt) => {
                            const on = newPanelTipo === opt.tipo_painel;
                            return (
                              <button
                                key={opt.id}
                                type="button"
                                className={['monitoramento-new-panel-type', on ? 'is-on' : ''].filter(Boolean).join(' ')}
                                aria-pressed={on}
                                onClick={() => { setNewPanelTipo(opt.tipo_painel); setCreateError(null); }}
                              >
                                {panelTypeIcon(opt.tipo_painel, 12)}
                                <span>{opt.label}</span>
                              </button>
                            );
                          })}
                        </div>
                        <button
                          type="button"
                          className="monitoramento-new-panel-action monitoramento-new-panel-action--confirm"
                          disabled={!canConfirmNew}
                          onClick={saveNewPanel}
                          title="Confirmar"
                          aria-label="Confirmar cadastro"
                        >
                          <Check size={14} strokeWidth={2.25} />
                        </button>
                        <button
                          type="button"
                          className="monitoramento-new-panel-action"
                          onClick={() => {
                            setIsNewPanel(false);
                            setNewPanelName('');
                            setNewPanelTipo('');
                            setCreateError(null);
                          }}
                          title="Cancelar"
                          aria-label="Cancelar cadastro"
                        >
                          <X size={14} strokeWidth={2.25} />
                        </button>
                      </div>
                    )}
                  </div>
                )}

                <span className="board-toolbar-separator" aria-hidden="true">|</span>

                {!activeIsMapAlive && !activeIsPreAtendimento ? (
                  <MonitoramentoDayFilter
                    selectedDate={selectedDate}
                    onApply={setSelectedDate}
                  />
                ) : null}

                {!activeIsPreAtendimento ? (
                  <MonitoramentoStatusFilter
                    options={statusOptions}
                    selectedIds={statusFilter}
                    onChange={setStatusFilter}
                  />
                ) : null}
              </div>

              <div className="search-container board-toolbar-right">
                <div className="board-toolbar-actions-group monitoramento-view-actions">
                  <MonitoramentoActionsMenu
                    onExportPdf={handleExportPdf}
                    onExportPng={handleExportPng}
                    onExportXlsx={handleExportXlsx}
                    exportDisabled={!activePanelId || panelsLoading || activeIsPreAtendimento}
                    showXlsx={activeIsExterno}
                  />
                  {canEdit ? (
                    <MonitoramentoModernTooltip
                      label="Configurar rotação automática no Modo TV"
                      placement="bottom"
                      className="monitoramento-modern-tooltip-host--chip"
                      hideOnClick
                    >
                      <button
                        type="button"
                        className={['board-clickup-filter-chip', canRotate ? 'has-selection' : ''].filter(Boolean).join(' ')}
                        onClick={() => setTvRotationModalOpen(true)}
                        aria-label="Rotação TV"
                      >
                        <span className="board-clickup-filter-chip-icon" aria-hidden="true">
                          <Repeat size={13} strokeWidth={1.75} />
                        </span>
                        <span className="board-clickup-filter-chip-text">
                          <span className="board-clickup-filter-chip-label">Rotação TV</span>
                        </span>
                      </button>
                    </MonitoramentoModernTooltip>
                  ) : null}
                  <button
                    type="button"
                    className="board-clickup-filter-chip"
                    onClick={toggleFullscreen}
                    title="Modo TV (tela cheia)"
                    aria-label="Modo TV"
                  >
                    <span className="board-clickup-filter-chip-icon" aria-hidden="true">
                      <Monitor size={13} strokeWidth={1.75} />
                    </span>
                    <span className="board-clickup-filter-chip-text">
                      <span className="board-clickup-filter-chip-label">Modo TV</span>
                    </span>
                  </button>
                </div>
              </div>
            </div>
          </div>

          {createError && canEdit ? (
            <div className="monitoramento-view-toolbar-error" role="alert">
              {createError}
            </div>
          ) : null}
        </div>
      )}
      <div
        className={`monitoramento-content${tvKeepAlive ? ' monitoramento-content--tv-rot' : ''}`}
      >
        {tvKeepAlive ? (
          (() => {
            const byId = new Map((panels || []).map((p) => [Number(p.id), p]));
            const list = [];
            const seen = new Set();
            const pushPanel = (p) => {
              if (!p || seen.has(Number(p.id))) return;
              seen.add(Number(p.id));
              list.push(p);
            };
            pushPanel(activePanel);
            rotationPlaylist.forEach((id) => pushPanel(byId.get(Number(id))));
            const activeNum = Number(activePanelId);
            const hasActive = list.some((p) => Number(p.id) === activeNum);
            return list.map((panel, idx) => {
              const isActive = hasActive ? Number(panel.id) === activeNum : idx === 0;
              const isMap = isMapAlivePanel(panel);
              const isPre = isPreAtendimentoPanel(panel);
              const slotKey = isPre ? 'pre' : isMap ? 'map' : 'ext';
              return (
                <div
                  key={`${slotKey}-${panel.id}`}
                  className={`monitoramento-tv-slot${isActive ? ' is-active' : ''}`}
                  aria-hidden={!isActive}
                >
                  {isPre ? (
                    <MonitoramentoPreAtendimentoDashboard
                      showTitle={fullscreen}
                      title={panel.nome_painel}
                      tvCountdown={isActive ? tvCountdown : null}
                      tvVisible={isActive}
                    />
                  ) : isMap ? (
                    <MonitoramentoMapAliveDashboard
                      showTitle={fullscreen}
                      title={panel.nome_painel}
                      tvCountdown={isActive ? tvCountdown : null}
                      tvVisible={isActive}
                      panelId={panel.id}
                      configRefreshKey={configRefreshKey}
                      statusFilter={statusFilter}
                      onStatusOptionsChange={isActive ? handleStatusOptionsChange : null}
                      exportRef={isActive ? dashboardExportRef : null}
                    />
                  ) : (
                    <MonitoramentoExternoDashboard
                      showTitle={fullscreen}
                      title={panel.nome_painel}
                      tvCountdown={isActive ? tvCountdown : null}
                      panelId={panel.id}
                      configRefreshKey={configRefreshKey}
                      selectedDate={selectedDate}
                      isLive={isLiveDate}
                      statusFilter={statusFilter}
                      onStatusOptionsChange={isActive ? handleStatusOptionsChange : null}
                      exportRef={isActive ? dashboardExportRef : null}
                      exportDataRef={isActive ? exportDataRef : null}
                    />
                  )}
                </div>
              );
            });
          })()
        ) : (
          <>
            {activePanelId && activeIsPreAtendimento && (
              <MonitoramentoPreAtendimentoDashboard
                key={`pre-${activePanelId}`}
                showTitle={fullscreen}
                title={panelTitle}
                tvCountdown={tvCountdown}
              />
            )}
            {activePanelId && activeIsMapAlive && (
              <MonitoramentoMapAliveDashboard
                key={`map-${activePanelId}`}
                showTitle={fullscreen}
                title={panelTitle}
                tvCountdown={tvCountdown}
                panelId={activePanelId}
                configRefreshKey={configRefreshKey}
                statusFilter={statusFilter}
                onStatusOptionsChange={handleStatusOptionsChange}
                exportRef={dashboardExportRef}
              />
            )}
            {activePanelId && activeIsExterno && (
              <MonitoramentoExternoDashboard
                key={`ext-${activePanelId}`}
                showTitle={fullscreen}
                title={panelTitle}
                tvCountdown={tvCountdown}
                panelId={activePanelId}
                configRefreshKey={configRefreshKey}
                selectedDate={selectedDate}
                isLive={isLiveDate}
                statusFilter={statusFilter}
                onStatusOptionsChange={handleStatusOptionsChange}
                exportRef={dashboardExportRef}
                exportDataRef={exportDataRef}
              />
            )}
          </>
        )}
        {!activePanelId && !panelsLoading && (
          <div className="monitoramento-content-empty">
            {panels.length === 0
              ? (canEdit ? 'Crie um painel para começar.' : 'Nenhum painel disponível.')
              : 'Selecione um painel.'}
          </div>
        )}
      </div>
      {fullscreen && (
        <button
          type="button"
          className={`monitoramento-tv-exit-btn${tvExitVisible ? ' is-visible' : ''}`}
          onClick={toggleFullscreen}
          title="Sair do modo TV"
          tabIndex={tvExitVisible ? 0 : -1}
          aria-hidden={!tvExitVisible}
        >
          Sair do modo TV
        </button>
      )}
      {canEdit && (
        <MonitoramentoTvRotationModal
          open={tvRotationModalOpen}
          onClose={() => setTvRotationModalOpen(false)}
          panels={panels}
          initialRotation={tvRotation}
          onSaved={(data) => {
            if (data) setTvRotation(normalizeTvRotation(data));
            else fetchTvRotation();
          }}
        />
      )}
      {canEdit && !activeIsPreAtendimento && (
        <MonitoramentoExternoConfigModal
          open={configModalOpen}
          onClose={() => setConfigModalOpen(false)}
          onSaved={() => {
            fetchPanels();
            fetchTvRotation();
            setConfigRefreshKey((k) => k + 1);
          }}
          panelId={activePanelId}
        />
      )}
    </div>
  );
}
